import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { cx } from "@/utils/cx";

/**
 * Classes de cor de um badge. Mesmo formato de
 * `InfractionStatusBadgePalette`, para que cada domínio continue dono do
 * mapeamento entre status e cor.
 */
export type StatusBadgePalette = {
	root: string;
	dot: string;
	label: string;
};

export type StatusBadgeTone =
	| "gray"
	| "success"
	| "warning"
	| "error"
	| "blue"
	| "indigo"
	| "orange";

/**
 * Paletas de apoio com os tokens utilitários usados em Infrações. Não carregam
 * significado de negócio: cada domínio escolhe a paleta de cada status.
 */
export const STATUS_BADGE_TONE_PALETTES: Record<
	StatusBadgeTone,
	StatusBadgePalette
> = {
	gray: {
		root: "border-utility-gray-200 bg-utility-gray-50",
		dot: "bg-utility-gray-500",
		label: "text-utility-gray-700",
	},
	success: {
		root: "border-utility-success-200 bg-utility-success-50",
		dot: "bg-utility-success-500",
		label: "text-utility-success-700",
	},
	warning: {
		root: "border-utility-warning-200 bg-utility-warning-50",
		dot: "bg-utility-warning-500",
		label: "text-utility-warning-700",
	},
	error: {
		root: "border-utility-error-200 bg-utility-error-50",
		dot: "bg-utility-error-500",
		label: "text-utility-error-700",
	},
	blue: {
		root: "border-utility-blue-200 bg-utility-blue-50",
		dot: "bg-utility-blue-500",
		label: "text-utility-blue-700",
	},
	indigo: {
		root: "border-utility-indigo-200 bg-utility-indigo-50",
		dot: "bg-utility-indigo-500",
		label: "text-utility-indigo-700",
	},
	orange: {
		root: "border-utility-orange-200 bg-utility-orange-50",
		dot: "bg-utility-orange-500",
		label: "text-utility-orange-700",
	},
};

export type StatusBadgeProps = Omit<
	ComponentPropsWithoutRef<"span">,
	"children"
> & {
	/** Texto do status. É obrigatório: a cor nunca é o único indicador. */
	label: ReactNode;
	/** Paleta definida pelo domínio. Padrão: `gray`. */
	palette?: StatusBadgePalette;
	/**
	 * Exibe o ponto de status. Use `false` em tags de categoria ou tipo, em que
	 * o ponto não representa um estado.
	 */
	showDot?: boolean;
};

/**
 * Badge de status compartilhado, com a geometria da coluna Status de
 * Infrações: cápsula com borda, `px-1.5 py-2`, `gap-1`, texto `xs` medium
 * com `leading-3` e ponto de 6 px.
 */
export function StatusBadge({
	label,
	palette = STATUS_BADGE_TONE_PALETTES.gray,
	showDot = true,
	className,
	...props
}: StatusBadgeProps) {
	return (
		<span
			className={cx(
				"inline-flex max-w-full items-center gap-1 rounded-full border px-1.5 py-2",
				palette.root,
				className
			)}
			data-slot="status-badge"
			{...props}
		>
			{showDot ? (
				<span
					aria-hidden
					className={cx("size-1.5 shrink-0 rounded-full", palette.dot)}
					data-slot="status-badge-dot"
				/>
			) : null}
			<span className={cx("font-medium text-xs leading-3", palette.label)}>
				{label}
			</span>
		</span>
	);
}
